import { Grid, html } from 'gridjs'
import 'gridjs/dist/theme/mermaid.css'

import Sortable from 'sortablejs'
import { initFetchPhoneButton } from './prospect'
import { initScheduleButtonListeners } from './utils/bots_prospects/init_schedule_button_listeners'
import { initNoteButtonListeners } from './utils/bots_prospects/init_note_button_listeners'

interface BotsProspectsData {
  bots_prospects: any[]
  total_count: number
}

interface GridState {
  instance: Grid | null
  rendered: boolean
  sortable?: Sortable | null
  columns: any[]
  rows: any[]
}

const gridState: GridState = {
  instance: null,
  rendered: false,
  sortable: null,
  columns: [],
  rows: [],
}

const fixedWidth = '220px'

let isNoteButtonListenersInitialized = false;
let isScheduleButtonListenersInitialized = false;

let BOTS_PROSPECTS_GRID_COLUMNS = [
  {
    id: 'name',
    name: 'Full Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { value: string, prospectData: any }, b: { value: string, prospectData: any }) => {
        try {
          return a.value.localeCompare(b.value);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { value: string, prospectData: any }) => {
      try {
        return html(formatExpandableCell(cell.value, "Full Name"));
      } catch {
        return "";
      }
    },
    attributes: (cell: { value: string, prospectData: any }) => {
      try {
        return {
          'data-prospect': JSON.stringify(cell.prospectData)
        }
      } catch {
        return {}
      }
    }
  },
  {
    id: 'schedule_reply',
    name: 'Actions',
    width: fixedWidth,
    sort: false,
  },
  {
    id: 'company',
    name: 'Current Company',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { name: string, domain: string }, b: { name: string, domain: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { name: string, domain: string }) => {
      try {
        return html(formatExpandableCellHTML(formatCompanyName(cell.name, cell.domain), 'Full Current Company'));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'position',
    name: 'Current Title',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Current Title")); } catch { return ""; }
    }
  },
  {
    id: 'location',
    name: 'Location',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Location")); } catch { return ""; }
    }
  },
  {
    id: 'social',
    name: 'Link to social profiles',
    width: fixedWidth,
    sort: false,
    formatter: (cell: string) => {
      try {
        const links = cell ? JSON.parse(cell) : {};
        return html(formatSocialLinks(links));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'first_contact',
    name: 'Time of first contact',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: string, b: string) => {
        const aDate = a ? new Date(a).getTime() : 0;
        const bDate = b ? new Date(b).getTime() : 0;
        return aDate - bDate;
      }
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of first contact")); } catch { return ""; }
    }
  },
  {
    id: 'last_contact',
    name: 'Time of last contact',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: string, b: string) => {
        const aDate = a ? new Date(a).getTime() : 0;
        const bDate = b ? new Date(b).getTime() : 0;
        return aDate - bDate;
      }
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of last contact")); } catch { return ""; }
    }
  },
  {
    id: 'response_date',
    name: 'Time of response',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: string, b: string) => {
        const aDate = a ? new Date(a).getTime() : 0;
        const bDate = b ? new Date(b).getTime() : 0;
        return aDate - bDate;
      }
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of response")); } catch { return ""; }
    }
  },
  {
    id: 'status',
    name: 'Email Status',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatStatus(cell)); } catch { return ""; }
    }
  },
  {
    id: 'reply',
    name: 'Reply',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Full Reply")); } catch { return ""; }
    }
  },
  {
    id: 'bot',
    name: 'Campaign Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { id: string, name: string }, b: { id: string, name: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { id: string, name: string }) => {
      try { return html(formatExpandableCellHTML(formatBotName(cell.id, cell.name), 'Full Campaign Name')); } catch { return ""; }
    }
  },
  {
    id: 'user_name',
    name: 'User',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "User")); } catch { return ""; }
    }
  }
]

let PHONE_DASHBOARD_GRID_COLUMNS = [
  {
    id: 'name',
    name: 'Full Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { value: string, prospectData: any }, b: { value: string, prospectData: any }) => {
        try {
          return a.value.localeCompare(b.value);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { value: string, prospectData: any }) => {
      try {
        return html(formatExpandableCell(cell.value, "Full Name"));
      } catch {
        return "";
      }
    },
    attributes: (cell: { value: string, prospectData: any }) => {
      try {
        return {
          'data-prospect': JSON.stringify(cell.prospectData)
        }
      } catch {
        return {}
      }
    }
  },
  {
    id: 'schedule_reply',
    name: 'Actions',
    width: fixedWidth,
    sort: false,
  },
  {
    id: 'company',
    name: 'Current Company',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { name: string, domain: string }, b: { name: string, domain: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { name: string, domain: string }) => {
      try {
        return html(formatExpandableCellHTML(formatCompanyName(cell.name, cell.domain), 'Full Current Company'));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'position',
    name: 'Current Title',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Current Title")); } catch { return ""; }
    }
  },
  {
    id: 'location',
    name: 'Location',
    width: fixedWidth,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Location")); } catch { return ""; }
    }
  },
  {
    id: 'social',
    name: 'Link to social profiles',
    width: fixedWidth,
    sort: false,
    formatter: (cell: string) => {
      try {
        const links = cell ? JSON.parse(cell) : {};
        return html(formatSocialLinks(links));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'status_calling',
    name: 'Status Calling',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(cell) } catch { return "" }
    }
  },
  {
    id: 'status',
    name: 'Email Status',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatStatus(cell)); } catch { return ""; }
    }
  },
  {
    id: 'bot',
    name: 'Campaign Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { id: string, name: string }, b: { id: string, name: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { id: string, name: string }) => {
      try { return html(formatExpandableCellHTML(formatBotName(cell.id, cell.name), 'Full Campaign Name')); } catch { return ""; }
    }
  },
  {
    id: 'user_name',
    name: 'User',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "User")); } catch { return ""; }
    }
  }
]

function createJsonApiUrl(url: string): string {
  const apiUrl = new URL(url)

  apiUrl.searchParams.set('format', 'json')

  return apiUrl.toString()
}

function createServerConfig(baseUrl: string, columns: any[]) {
  return {
    url: createJsonApiUrl(baseUrl),
    then: (data: BotsProspectsData) => {

      const rows = data.bots_prospects.map((prospect: any) => {
        const prospectData = {
          id: prospect.id,
          botId: prospect.bot_id,
          name: prospect.name || 'N/A',
          location: prospect.location || 'N/A',
          companyName: prospect.company_name || 'N/A',
          companyDomain: prospect.company_domain || 'N/A',
          position: prospect.position || 'N/A',
          education: prospect.education || 'No education data.',
          skills: prospect.skills || [],
          languages: prospect.languages || [],
        };

        return {
          name: {
            value: prospect.name ?? '',
            prospectData,
          },
          schedule_reply: html(actionButtons(prospect.action_buttons_data)),
          company: {
            name: prospect.company_name,
            domain: prospect.company_domain,
          },
          position: prospect.position || '',
          location: prospect.location || 'N/A',
          social: JSON.stringify(prospect.social_links),
          first_contact: prospect.first_contact_date || '',
          last_contact: prospect.last_contact_date || '',
          response_date: prospect.response_date || '',
          status: prospect.status,
          status_calling: prospect.status_calling,
          reply: prospect.reply || '',
          bot: {
            id: prospect.bot_id,
            name: prospect.bot_name,
          },
          user_name: prospect.user_name || '',
        }
      })

      // Schedule DOM updates after render
      setTimeout(() => {
        const wrapper = document.getElementById('candidates-table')
        const tbody = wrapper?.querySelector('tbody')
        if (tbody && tbody.querySelectorAll('tr').length > 0) {
          if (!isNoteButtonListenersInitialized) {
            initNoteButtonListeners(columns.length);
            isNoteButtonListenersInitialized = true;
          }
          if (!isScheduleButtonListenersInitialized) {
            initScheduleButtonListeners(columns.length);
            isScheduleButtonListenersInitialized = true;
          }
        }
        $('[data-toggle="popover"]').popover({ html: true, sanitize: false, trigger: 'click' });
        initFetchPhoneButton();
        initSortableColumns();
        updateExpandableCells();
      }, 300)

      gridState.rows = rows

      return rows.map(r => gridState.columns.map(c => r[c.id]))
    },
    total: (data: BotsProspectsData) => data.total_count || 0
  }
}

function initSortableColumns() {
  if (gridState.sortable) {
    try { gridState.sortable.destroy() } catch { }
    gridState.sortable = null
  }

  const headerRow = document.querySelector('.gridjs-thead tr') as HTMLElement
  const bodyRows = document.querySelectorAll('.gridjs-tbody tr') as NodeListOf<HTMLElement>

  gridState.sortable = Sortable.create(headerRow, {
    animation: 150,
    scrollSensitivity: 100,
    forceFallback: false, // prevent buggy/freeze animations
    draggable: 'th.gridjs-th',
    handle: '.gridjs-th-content',
    onMove: (evt) => {
      if (evt.related == null || evt.dragged == null) {
        return
      }

      const oldIndex = evt.dragged?.cellIndex
      const newIndex = evt.related?.cellIndex
      if (oldIndex === newIndex) {
        return
      }

      bodyRows.forEach((row) => {
        const cells = row.children
        if (!cells[oldIndex] || !cells[newIndex]) {
          return
        }

        if (oldIndex < newIndex) {
          row.insertBefore(cells[oldIndex], cells[newIndex].nextSibling)
        } else {
          row.insertBefore(cells[oldIndex], cells[newIndex])
        }

        cells[oldIndex].innerHTML == ''
        cells[newIndex].innerHTML == ''
      })
    },
    onEnd: (evt) => {
      if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex == evt.newIndex) {
        return
      }

      const moved = gridState.columns.splice(evt.oldIndex, 1)[0]
      gridState.columns.splice(evt.newIndex, 0, moved)
      gridState.instance.updateConfig({
        columns: gridState.columns
      }).forceRender();
    }
  })
}

function createPaginationConfig() {
  return {
    limit: 100,
    server: {
      url: (prev: string, page: number, limit: number) => {
        const url = new URL(prev)

        url.searchParams.set('page', String(page + 1))
        url.searchParams.set('per_page', String(limit))

        return url.href
      }
    }
  }
}

function changePaginationLimit(newLimit: number): void {
  const wrapper = document.getElementById('candidates-table')
  if (!wrapper) return

  if (gridState.instance && gridState.rendered) {
    const columns = wrapper.getAttribute('data-phone-dashboard') ? PHONE_DASHBOARD_GRID_COLUMNS : BOTS_PROSPECTS_GRID_COLUMNS

    gridState.instance
      .updateConfig({
        pagination: {
          limit: newLimit,
          server: {
            url: (prev: string, page: number, limit: number) => {
              const url = new URL(prev)

              url.searchParams.set('page', String(page + 1))
              url.searchParams.set('per_page', String(limit))

              return url.href
            }
          }
        },
        server: createServerConfig(window.location.href, columns)
      })

    wrapper.innerHTML = ''

    gridState.instance.render(wrapper).forceRender()
  } else {
    initBotsProspectsGrid(newLimit)
  }
}

if (typeof window !== 'undefined') {
  (window as typeof window & { changePaginationLimit?: (limit: number) => void }).changePaginationLimit = changePaginationLimit
}

export function initBotsProspectsGrid(paginationLimit: number = 100): void {
  const wrapper = document.getElementById('candidates-table')
  if (!wrapper) return

  let columns = []
  if (columns.length === 0) {
    columns = wrapper.getAttribute('data-phone-dashboard') ? PHONE_DASHBOARD_GRID_COLUMNS : BOTS_PROSPECTS_GRID_COLUMNS
    gridState.columns = [...columns];
  } else {
    columns = gridState.columns;
  }

  const grid = new Grid({
    columns: gridState.columns,
    server: createServerConfig(window.location.href, columns),
    fixedHeader: true,
    pagination: {
      limit: paginationLimit,
      ...createPaginationConfig()
    },
  })

  wrapper.innerHTML = ''

  grid.render(wrapper)

  // Force rerender to prevent unclickable sort-columns
  grid.forceRender();

  // Store the grid instance for later use
  gridState.instance = grid
  gridState.rendered = true

  grid.config.store.subscribe((state: any) => {
    if (state.sort) {
      $('.summernote').summernote('destroy');

      const initialForms = wrapper.querySelectorAll('.all-candidates-schedule-form');
      initialForms.forEach((form) => {
        form.remove();
      });
      const scheduleButtons = wrapper.querySelectorAll('.schedule-button.active');
      scheduleButtons.forEach((button) => {
        button.classList.remove('active');
      });

      const noteRows = wrapper.querySelectorAll('.all-candidates-note-form');
      noteRows.forEach((row) => {
        row.remove();
      })
      const noteButtons = wrapper.querySelectorAll('.notes-button.active');
      noteButtons.forEach((button) => {
        button.classList.remove("active");
      })
    }
  });
}

function formatCompanyName(companyName: string, companyDomain: string): string {
  if (!companyName) return ''

  const url = `https://${companyDomain}`

  return `
    <a
      href="${url}"
      target="_blank"
      rel="noopener noreferrer"
      class="text-primary"
      style="text-decoration: none;"
    >
      ${companyName}
    </a>
  `
}

function formatExpandableCellHTML(innerHTML: string, title: string = 'Full Data'): string {
  if (!innerHTML) return ''
  const escapedHTML = innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  return `
    <div 
      class="expandable-cell"
      style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
    >
      <div
        class="short-content text-truncate"
        style="max-width: 150px;"
      >
        ${innerHTML}
      </div>
        <span
          class="view-full-data"
          data-content="${escapedHTML}"
          data-title="${title}"
          style="cursor: pointer; color: #007bff; font-size: 14px; display: none;"
          title="View full message">
          <i class="fas fa-expand-alt"></i>
      </span>
    </div>
  `
}

function formatExpandableCell(text: string, title: string = 'Full Information'): string {
  if (!text) return ''
  const escapedText = text.replace(/"/g, '&quot;').replace(/'/g, '&#39;')

  return `
    <div
      class="expandable-cell"
      style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
    >
      <div
        class="short-content text-truncate ${title == 'Full Name' ? 'view-full-profile' : ''}"
        style="max-width: 150px;"
      >
        ${escapedText}
      </div>
      <span
        class="view-full-data"
        data-content="${escapedText}"
        data-title="${title}"
        style="cursor: pointer; color: #007bff; font-size: 14px; display: none;"
        title="View full message"
      >
        <i class="fas fa-expand-alt"></i>
      </span>
    </div>
  `
}

function formatSocialLinks(links: any): string {
  if (!links || Object.keys(links).length === 0) return ''

  const socialPlatforms = [
    { key: 'linkedin', icon: 'fab fa-linkedin' },
    { key: 'github', icon: 'fab fa-github' },
    { key: 'stack_overflow', icon: 'fab fa-stack-overflow' },
    { key: 'meetup', icon: 'fab fa-meetup' },
    { key: 'hacker_news', icon: 'fab fa-hacker-news' },
    { key: 'facebook', icon: 'fab fa-facebook' },
    { key: 'twitter', icon: 'fab fa-twitter' },
    { key: 'quora', icon: 'fab fa-quora' },
    { key: 'angellist', icon: 'fab fa-angellist' }
  ]

  const linkElements = socialPlatforms
    .filter(platform => links[platform.key])
    .map(platform => {
      let icon = `<i class="${platform.icon}"></i>`;
      if (platform.key === 'twitter') {
        icon = `<img src="/x-twitter-brands-solid-full.svg" alt="Twitter" width="25px" height="25px" />`
      }

      return `<a href="${links[platform.key]}" target="_blank" class="personalize-external-link" style="display: flex; align-items: center;">
        ${icon}
      </a>`
    })
    .join('')

  return `<div class="personalize-icons">${linkElements}</div>`
}

function formatStatus(status: string): string {
  if (!status) return ''

  const statusClasses = {
    'interested': 'badge-success',
    'responded': 'badge-primary',
    'opened': 'badge-info',
    'bounced': 'badge-danger',
    'contacted': 'badge-secondary',
    'not_contacted': 'badge-dark'
  } as const

  const badgeClass = statusClasses[status.toLowerCase() as keyof typeof statusClasses] || 'badge-secondary'

  return `
    <span
      class="badge ${badgeClass}"
      style="
        padding: 4px 8px;
        font-size: 12px;
        border-radius: 6px;
      "
    >
      ${status}
    </span>
  `
}

function actionButtons(data?: any): string {
  return `
    <div class="action-buttons d-flex">
      ${data.schedule_button}
      ${data.linkedin_button}
      ${data.phone_button}
      ${data.note_button}
    </div>
  `
}

function formatBotName(botId: string, botName: string): string {
  if (!botId || !botName) return ''

  const path = `/bots/${botId}`

  return `<a href="${path}" class="text-primary" style="text-decoration: none;">${botName}</a>`
}

function updateExpandableCells() {
  const cells = document.querySelectorAll<HTMLElement>('.expandable-cell');
  cells.forEach(cell => {
    const content = cell.querySelector<HTMLElement>('.short-content');
    const span = cell.querySelector<HTMLElement>('.view-full-data');
    if (!content || !span) return;
    if (content.scrollWidth > content.clientWidth) {
      span.style.display = 'inline-block';
    } else {
      span.style.display = 'none';
    }
  });
}
